"use client";

import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { SectionHeading } from "@/components/ui/section-heading";
import { LogoMark } from "@/components/ui/logo";
import { PUBLIC_API_V1_HOST, PUBLIC_API_V1_URL } from "@/lib/api/public-url";
import { ease } from "@/lib/motion";

// our P/9 model aliases (each backed by a leading open-source model)
const ALIASES = ["p9-coder", "p9-large", "p9-base", "p9-reason", "p9-fast", "p9-embed"];

// the same endpoint works with any OpenAI-compatible SDK / client
const SDKS = [
  "from openai import OpenAI",
  'import OpenAI from "openai"',
  "from langchain_openai import ChatOpenAI",
  "from litellm import completion",
];

const MARKERS = ["Drop-in OpenAI-compatible", "Sovereign infrastructure", "Billed locally"];

const lineV = {
  hidden: { opacity: 0, x: -8 },
  show: { opacity: 1, x: 0, transition: { duration: 0.4, ease } },
};

// monochrome syntax tones
const str = "text-foreground";
const com = "italic text-muted-2";
const pun = "text-muted";

export function UnifiedApi() {
  const reduce = useReducedMotion();
  const [mi, setMi] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => setMi((v) => (v + 1) % 12), 2200);
    return () => clearInterval(id);
  }, [reduce]);

  const alias = ALIASES[mi % ALIASES.length];
  const sdk = SDKS[mi % SDKS.length];

  return (
    <section
      id="platform"
      className="relative mx-auto max-w-[90rem] px-5 py-16 sm:px-8 sm:py-32"
    >
      <SectionHeading
        eyebrow="3. One unified API"
        title={
          <>
            One OpenAI-compatible API, connected to optimised open-source models on
            sovereign infrastructure.
          </>
        }
      />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7, ease }}
        className="mx-auto mt-14 max-w-2xl"
      >
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]">
          {/* header */}
          <div className="flex items-center gap-2 border-b border-border px-5 py-3 text-foreground">
            <LogoMark className="h-4 w-4" />
            <span className="font-mono text-xs text-muted">
              {PUBLIC_API_V1_HOST}
            </span>
          </div>

          {/* code */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            transition={{ staggerChildren: 0.06, delayChildren: 0.2 }}
            className="overflow-x-auto px-5 py-5 font-mono text-[0.74rem] leading-relaxed sm:text-[0.82rem]"
          >
            <div className="w-max min-w-full">
              <motion.div variants={lineV} className={com}>
                # drop-in with any OpenAI-compatible SDK
              </motion.div>
              <motion.div variants={lineV} className="min-h-[1.5em]">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={sdk}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.3, ease }}
                    className="inline-block text-foreground/85"
                  >
                    {sdk}
                  </motion.span>
                </AnimatePresence>
              </motion.div>
              <motion.div variants={lineV} className="h-3" aria-hidden />

              <motion.div variants={lineV}>
                <span className="inline-block w-[8ch] text-foreground/85">base_url</span>
                <span className={pun}>= </span>
                <span className={str}>&quot;{PUBLIC_API_V1_URL}&quot;</span>
              </motion.div>
              <motion.div variants={lineV}>
                <span className="inline-block w-[8ch] text-foreground/85">api_key</span>
                <span className={pun}>= </span>
                <span className={str}>&quot;sk_p9_•••••••&quot;</span>
              </motion.div>
              <motion.div variants={lineV} className="flex items-center">
                <span className="inline-block w-[8ch] text-foreground/85">model</span>
                <span className={pun}>= </span>
                <span className="relative inline-flex rounded bg-foreground/[0.07] px-1 text-foreground">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={alias}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.3, ease }}
                    >
                      &quot;{alias}&quot;
                    </motion.span>
                  </AnimatePresence>
                </span>
              </motion.div>
              <motion.div variants={lineV} className="h-3" aria-hidden />

              <motion.div variants={lineV} className={com}>
                # served on sovereign compute · billed locally in PKR
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* trust markers */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
          {MARKERS.map((m, i) => (
            <span key={m} className="flex items-center gap-x-3 font-sans text-sm text-muted">
              {i > 0 && <span aria-hidden className="text-muted-2">·</span>}
              <span
                tabIndex={0}
                className="group relative cursor-default outline-none transition-colors hover:text-foreground focus:text-foreground"
              >
                {m}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-foreground/35 transition-all duration-300 group-hover:w-full group-focus:w-full" />
              </span>
            </span>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
